import { useCallback, useState, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';

import { COLUMN_COLOURS } from '../columns.constants';
import {
  ColumnProperties,
  ColumnStyle,
  LAYOUT_NO_STYLE,
  SettingProps,
} from './column-settings.constants';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import {
  getColumnLayoutProperties,
  getColumnsProperties,
} from './column-settings.utils';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { ColourTokenRadio } from 'approot/shared/colour/colour-token';
import Switch from 'approot/shared/switch/switch';
import { BORDER_STYLE_BOX, BORDER_STYLE_NONE } from 'global/constants';
import { Dropdown, DropdownIndicator } from 'approot/shared/dropdown/dropdown';
import {
  ColumnSettingsGroup,
  ColumnDropdownButton,
  ColumnDropdownSwitch,
  ColumnSettingsBordersTabsWrapper,
} from './column-settings.style';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import { SETTINGS_MENU_ID } from 'approot/activity-builder/settings/settings-component-menu';
import { ComponentColumn } from '../../component.constants';
import { updateExistingColumnComponent } from 'approot/activity-builder/activity-builder.apollo.utils';
import Tabs from 'approot/shared/tabs/tabs';
import { getStyleProperties } from 'approot/activity-builder/components/components.utils';
import type { ComponentStyleInput } from '../../../../../gql/schema-types';

enum ActiveTab {
  Item,
  Column,
  Row,
}

const ColumnSettingsBorders = ({
  selectedColumn,
  selectedComponentId,
  columnProperties,
  columns,
  id,
  sectionId,
}: SettingProps & {
  selectedColumn?: string;
  selectedComponentId?: string;
}) => {
  const client = useApolloClient();
  const [activeTab, setActiveTab] = useState<ActiveTab>(
    selectedColumn === undefined ? ActiveTab.Row : ActiveTab.Item
  );

  const currentComponentStyle = useMemo(() => {
    if (activeTab === ActiveTab.Item && selectedComponentId && selectedColumn) {
      const component = columns
        ?.find(c => c.columnId === selectedColumn)
        ?.components.find(c => c.id === selectedComponentId);

      return component ? getStyleProperties(component) : undefined;
    }
  }, [activeTab, selectedColumn, selectedComponentId, columns]);

  const currentColumnsStyle = useMemo(() => {
    if (activeTab === ActiveTab.Column && selectedColumn) {
      return (
        columns?.find(c => c.columnId === selectedColumn)?.style ||
        LAYOUT_NO_STYLE
      );
    }

    return columnProperties?.style || LAYOUT_NO_STYLE;
  }, [activeTab, selectedColumn, columns, columnProperties]);

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const handleItemUpdate = useCallback(
    (style: Partial<ComponentStyleInput>) => {
      if (!selectedComponentId || !selectedColumn) return;

      const component = columns
        ?.find(c => c.columnId === selectedColumn)
        ?.components.find(c => c.id === selectedComponentId);

      if (!component) return;

      updateComponentProperties({
        isSaveImmediate: true,
        input: {
          sectionId,
          componentId: selectedComponentId,
          componentData: {
            properties: {
              style,
            },
          },
        },
        typename: component.__typename,
      });
    },
    [
      selectedComponentId,
      selectedColumn,
      sectionId,
      updateComponentProperties,
      columns,
    ]
  );

  const handleRowUpdate = useCallback(
    (style: Partial<ColumnStyle>) => {
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              style: {
                baseColour: style.baseColour,
                border: style.border,
                backgroundColour: style.backgroundColour,
              },
            },
          },
        },
        properties: {
          __typename: 'ColumnsComponentProperties',
          style: {
            __typename: 'ComponentStyle',
            baseColour: style.baseColour || null,
            backgroundColour: style.backgroundColour || null,
            border: style.border || null,
          },
        },
        typename: 'ActivitiesColumnsComponent',
      });
    },
    [id, sectionId, updateComponentProperties]
  );

  const handleUpdate = useCallback(
    ({
      style,
      columns,
      columnProperties,
    }: {
      columns: ComponentColumn[] | null;
      columnProperties: ColumnProperties | null;
      style: ColumnStyle;
    }) => {
      if (!columns) return;

      const columnStyle = getColumnLayoutProperties(
        style,
        columnProperties,
        !!selectedColumn
      );
      const columnsStyle = getColumnsProperties({
        style,
        applyToAll: false,
        columnId: selectedColumn,
        columns,
      });

      columnsStyle.forEach(col => {
        updateExistingColumnComponent({
          client,
          data: col,
          id: col.columnId,
          typename: 'ActivitiesColumnComponent',
        });
      });

      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              style: columnStyle,
              columns: columnsStyle,
            },
          },
        },
        properties: {
          __typename: 'ColumnsComponentProperties',
          style: {
            __typename: 'ComponentStyle',
            baseColour: columnStyle?.baseColour || null,
            backgroundColour: columnStyle?.backgroundColour || null,
            border: columnStyle?.border || null,
          },
        },
        typename: 'ActivitiesColumnsComponent',
      });
    },
    [client, selectedColumn, id, sectionId, updateComponentProperties]
  );

  const hasBorders =
    activeTab === ActiveTab.Item
      ? !!currentComponentStyle?.outerBorder
      : currentColumnsStyle?.border !== BORDER_STYLE_NONE;
  const hasBackground =
    activeTab === ActiveTab.Item
      ? !!currentComponentStyle?.outerBackgroundColour
      : !!currentColumnsStyle.backgroundColour;
  const activeColour =
    activeTab === ActiveTab.Item
      ? currentComponentStyle?.outerBaseColour
      : currentColumnsStyle?.baseColour;

  return (
    <Dropdown
      isSmall
      menuWidth={226}
      menuContainerElement={SETTINGS_MENU_ID}
      strategy="fixed"
      closeMenuOnSelect={false}
      dropdownButtonContents={
        <ColumnDropdownButton data-selector="column-borders">
          <ActivityBuilderInlineSvg
            id="icon_paint_pallet"
            width={20}
            height={20}
          />
          <DropdownIndicator />
        </ColumnDropdownButton>
      }
      dropdownContents={
        <>
          {selectedColumn && (
            <ColumnSettingsBordersTabsWrapper>
              <Tabs
                ariaDescription="Column styling tabs"
                contentPanelId="content"
                options={[
                  { label: 'Item', value: ActiveTab.Item },
                  { label: 'Column', value: ActiveTab.Column },
                ]}
                activeTab={activeTab}
                onSelect={tab => setActiveTab(tab.value as ActiveTab)}
                fillWidth={true}
              />
            </ColumnSettingsBordersTabsWrapper>
          )}
          <SettingsGroup as="fieldset" marginBottom={0}>
            <ColumnDropdownSwitch>
              <span>Borders</span>
              <Switch
                data-dropdown-item="true"
                ariaLabel={`Click to ${hasBorders ? 'hide' : 'show'} border`}
                state={hasBorders}
                onClick={() => {
                  if (activeTab === ActiveTab.Item) {
                    handleItemUpdate({
                      ...currentComponentStyle,
                      outerBorder: !hasBorders,
                      outerBaseColour:
                        currentComponentStyle?.outerBaseColour ||
                        COLUMN_COLOURS[0].base,
                    });
                  } else if (activeTab === ActiveTab.Column) {
                    handleUpdate({
                      columns,
                      columnProperties,
                      style: {
                        border: hasBorders
                          ? BORDER_STYLE_NONE
                          : BORDER_STYLE_BOX,
                        baseColour:
                          currentColumnsStyle?.baseColour ||
                          COLUMN_COLOURS[0].base,
                        backgroundColour: currentColumnsStyle?.backgroundColour,
                        __typename: 'ComponentStyle',
                      },
                    });
                  } else if (activeTab === ActiveTab.Row) {
                    handleRowUpdate({
                      border: hasBorders ? BORDER_STYLE_NONE : BORDER_STYLE_BOX,
                      baseColour:
                        currentColumnsStyle?.baseColour ||
                        COLUMN_COLOURS[0].base,
                    });
                  }
                }}
              />
            </ColumnDropdownSwitch>
            <ColumnDropdownSwitch>
              <span>Background</span>
              <Switch
                data-dropdown-item="true"
                ariaLabel={`Click to ${
                  hasBackground ? 'hide' : 'show'
                } background`}
                state={hasBackground}
                onClick={() => {
                  if (activeTab === ActiveTab.Item) {
                    handleItemUpdate({
                      ...currentComponentStyle,
                      outerBackgroundColour: !hasBackground,
                      outerBaseColour:
                        currentComponentStyle?.outerBaseColour ||
                        COLUMN_COLOURS[0].base,
                    });
                  } else if (activeTab === ActiveTab.Column) {
                    handleUpdate({
                      columns,
                      columnProperties,
                      style: {
                        backgroundColour: !hasBackground,
                        baseColour:
                          currentColumnsStyle?.baseColour ||
                          COLUMN_COLOURS[0].base,
                        border: currentColumnsStyle?.border,
                        __typename: 'ComponentStyle',
                      },
                    });
                  } else if (activeTab === ActiveTab.Row) {
                    handleRowUpdate({
                      backgroundColour: !hasBackground,
                      baseColour:
                        currentColumnsStyle?.baseColour ||
                        COLUMN_COLOURS[0].base,
                      border: currentColumnsStyle?.border,
                    });
                  }
                }}
              />
            </ColumnDropdownSwitch>
          </SettingsGroup>

          {(hasBorders || hasBackground) && (
            <ColumnSettingsGroup
              as="fieldset"
              marginBottom={selectedColumn ? 12 : 0}
            >
              <SettingsLabel as="legend">Colour</SettingsLabel>
              <div role="radiogroup">
                {COLUMN_COLOURS.map(item => (
                  <ColourTokenRadio
                    key={item.colour1}
                    name="colourField"
                    data-dropdown-item="true"
                    colours={item}
                    isActive={item.base === activeColour}
                    onChange={() => {
                      if (activeTab === ActiveTab.Item) {
                        handleItemUpdate({
                          ...currentComponentStyle,
                          outerBaseColour: item.base,
                        });
                      } else if (activeTab === ActiveTab.Column) {
                        handleUpdate({
                          columns,
                          columnProperties,
                          style: {
                            ...currentColumnsStyle,
                            baseColour: item.base,
                            border: currentColumnsStyle?.border,
                            backgroundColour:
                              currentColumnsStyle?.backgroundColour,
                            __typename: 'ComponentStyle',
                          },
                        });
                      } else if (activeTab === ActiveTab.Row) {
                        handleRowUpdate({
                          ...currentColumnsStyle,
                          baseColour: item.base,
                        });
                      }
                    }}
                  />
                ))}
              </div>
            </ColumnSettingsGroup>
          )}
        </>
      }
    />
  );
};

export default ColumnSettingsBorders;
